





































import {Vue, Component, Prop} from 'vue-property-decorator'
import {Action, State} from 'vuex-class'
import Utils from '../../utils'
import {EventBus} from '@/main'
import {DialogState, PopupState} from '../../store'
import moment from 'moment'
import {
  BookRequest,
  VehicleSlot,
  AvailabilityRequest,
  AvailabilityResponse,
  ReservationResponse,
  BookTimeSelectRequest,
} from '@/lib/kepler/interfaces'
import VehicleBookingDateTimeSelect from './VehicleBookingDateTimeSelect.vue'
import DateHelper from '@/lib/DateHelper'
import {ProfileState} from '@/store/modules/profile'
import VehicleBookingAddMemoDialog from '@/views/Vehicle/VehicleBookingAddMemoDialog.vue'
import VehicleConfirmBooking from '@/views/Vehicle/VehicleConfirmBooking.vue'
import QuickBookDialog from '@/components/QuickBookDialog.vue'
import ServiceMesh from '@/lib/serviceMesh'
import StatusLabel from '@/components/StatusLabel.vue'
import FuelLevel from '@/components/FuelLevel.vue'
import {VSpacer} from 'vuetify/lib'

@Component({
  components: {
    FuelLevel,
    StatusLabel,
    VSpacer,
    Card: Utils.loadComponent('proxy/Card/Card'),
    Container: Utils.loadComponent('proxy/Container'),
    Layout: Utils.loadComponent('proxy/Layout'),
    Flex: Utils.loadComponent('proxy/Flex'),
    BookingButtons: Utils.loadComponent('BookingButtons'),
    VehicleQuickInfo: Utils.loadComponent('entities/vehicle/VehicleQuickInfo'),
    VehicleLotAndDistance: Utils.loadComponent('entities/vehicle/VehicleLotAndDistance'),
    AccordionContent: Utils.loadComponent('proxy/Accordion/AccordionContent'),
    VehicleFares: Utils.loadComponent('entities/vehicle/VehicleFares'),
    VehicleInstructions: Utils.loadComponent('VehicleInstructions'),
    Divider: Utils.loadComponent('proxy/Divider'),
  },
  name: 'Vehicle',
})

export default class Vehicle extends Vue {
  @State('profile') public profileState!: ProfileState
  @State((state) => state.configuration.appConfig.fields_configuration) public fieldConfigState!: any
  @State((state) => state.configuration.appConfig.time_slot_dimension) public timeSlot!: any

  @Action('openPopup') public openPopup!: (popup: PopupState) => void
  @Action('openDialog') public openDialog!: (dialog: DialogState) => void
  @Action('closeDialog') public closeDialog!: (index?: number) => void

  @Action('setZone') public setZone!: (id: string) => Promise<void>

  @Action('vehicleAvailability') public vehicleAvailability: any
  @Action('book') public bookAction!: (payload: BookRequest) => Promise<ReservationResponse>
  @Action('selectReservation') public selectReservation!: (payload: ReservationResponse) => void

  @Prop({required: true, type: Object}) public vehicleSlot!: VehicleSlot

  public reservation?: ReservationResponse
  public bookRequest: BookRequest = {start: null, end: null, memo: null, plate: null}

  protected availability: AvailabilityResponse[] = []
  protected availabilityString: string | null = null
  protected availabilityColor: string = 'grey'

  protected loading: boolean = true

  public created() {
    this.setZone(this.vehicleSlot.id)
    this.bookRequest.plate = this.vehicleSlot.vehicle.plate
    EventBus.$on('quickBookingDateSelected', () => {
      this.closeDialog()
      this.book()
    })
  }

  public mounted() {
    this.getAvailability().finally(() => {
      this.getAvailabilityString()
      this.loading = false
    })

  }

  protected destroyed() {
    EventBus.$off('quickBookingDateSelected')
  }

  protected sendBooking(callback?: () => void) {
    this.bookAction(this.bookRequest)
      .then((r) => {
        this.reservation = r
        // console.log(r)
        this.openPopup(new PopupState(
          VehicleConfirmBooking, {vehicleSlot: this.vehicleSlot, confirmCallback: callback}, null, true))
      })
      .catch(() => {
        return '💩'
      })
  }

  protected book(callback?: () => void) {
    return this.openDialog(new DialogState(VehicleBookingAddMemoDialog, {
      vehicle: this.vehicleSlot.vehicle,
      bookRequest: this.bookRequest,
      confirmCallback: () => {
        if (callback !== undefined) {
          this.sendBooking(callback)
        } else {
          this.sendBooking(() => {
            if (this.reservation) {
              this.selectReservation(this.reservation)
            }
            this.$router.push({name: 'reservation'})
          })
        }
      },
    }))
  }

  protected booking() {
    this.openDialog(new DialogState(VehicleBookingDateTimeSelect, {
      vehicleSlot: this.vehicleSlot,
      bookRequest: this.bookRequest,
    }, null, false, 'quickBookingDateSelected'))
  }

  protected quickBooking() {
    this.loading = true

    const openQuickBooking = () => {
      this.openDialog(new DialogState(QuickBookDialog, {
        vehicleSlot: this.vehicleSlot,
        availability: this.availability,
        onConfirm: ((req: BookTimeSelectRequest) => {
          this.bookRequest.start = req.start
          this.bookRequest.end = req.end
          this.closeDialog()
          this.book()
        }),
      }, null, false))
    }
    const availabilityStillValid = moment().isBefore(this.availability[0].end)

    if (availabilityStillValid) {
      openQuickBooking()
      this.loading = false
    } else {
      this.getAvailability().then(openQuickBooking).finally(() => {
        this.loading = false
      })
    }
  }

  protected getAvailability() {
    const now = DateHelper.parse(DateHelper.now('YYYY-MM-DD HH:mm:ss'))
    const start = now.minute(now.minute() - now.minute() % this.timeSlot).seconds(0).unix()
    const end = now.add(2, 'days').unix()
    const ar: AvailabilityRequest = {
      vehicle_id: this.vehicleSlot.vehicle.id,
      start,
      end,
    }
    return this.vehicleAvailability(ar).then((r: AvailabilityResponse[]) => {
      this.availability = r
      this.availabilityString = this.getAvailabilityString()
    })
  }

  protected get vehicleTypeColor() {
    const key = `${this.vehicleSlot.reservation_type}${this.vehicleSlot.vehicle.category.type}`.toUpperCase()
    return {color: ServiceMesh.colors[key]}
  }

  protected get costString() {
    return `${this.$currency(this.vehicleSlot.base_rate.time_rate.driving_time_cost)}/${this.parseMinutes(this.vehicleSlot.base_rate.time_rate.time_cost_cycle)} - ${this.vehicleSlot.base_rate.distance_rate.distance_cost}/${this.$distance()}`
  }

  protected getAvailabilityString() {
    const formatString = (n: number) => {
      return moment.unix(this.availability[n].start_timestamp).calendar()
    }

    const countWhile = (value: boolean, len: number) => {
      let n = 1
      while (!!this.availability[n]?.available === value && n <= len) {
        n++
      }
      return n
    }

    const l = this.availability?.length
    const first = this.availability[0]?.available

    if (l && first) {
      const n = countWhile(true, l)
      if (n < l) {
        this.availabilityColor = 'warning'
        return this.$t('status.vehicle.available_to', {t: formatString(n)})
      } else {
        this.availabilityColor = 'success'
        return this.$t('status.vehicle.available')
      }
    } else if (l && !first) {
      const n = countWhile(false, l)
      if (n < l) {
        this.availabilityColor = 'warning'
        return this.$t('status.vehicle.available_from', {t: formatString(n)})
      } else {
        this.availabilityColor = 'error'
        return this.$t('status.vehicle.unavailable')
      }
    }
    return null
  }

  protected parseMinutes(val: number) {
    return DateHelper.parseMinutes(this, val)
  }
}
